import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { TESTFLIGHT_LINK } from "../lib/appLink";
import { signUpCoach } from "../lib/signUp";
import { oauthErrorMessage, oauthRedirectTarget } from "../lib/oauth";
import { opensOnSignUp } from "../lib/site";
import { BrandLockup } from "../components/BrandLockup";
import { GoogleMark } from "../components/GoogleMark";

/* The sign-in screen — the first screen anyone ever sees, and therefore the one place where
 * looking like everyone else costs the most.
 *
 * Every decision below traces to a verdict in `docs/EVERFIT-MAP.md` §1d, where the competitor's
 * own auth pages were read screen by screen and marked KEEP / CHANGE / DISCARD. The three that
 * shaped this file:
 *
 *  • DISCARD the stock-photo split layout. It is the default SaaS auth page — a photograph
 *    nobody looks at twice, a white card, a blue link. This project owns something better and
 *    has never worn it on the web: warm washi against sumi ink, shu vermilion, outlined buttons.
 *
 *  • CHANGE the greyed-out submit button. Everfit disables it until the form is valid; the map
 *    calls this "the first place in the map where copying them would make ours worse", and it is
 *    right for reasons beyond taste — a disabled button gives no reason, tells you nothing about
 *    WHICH field is wrong, and in several implementations cannot be reached by a screen reader
 *    at all. So the button here is ALWAYS enabled, validates on press, and says what is missing.
 *
 *  • KEEP the password-reveal asymmetry: sign-UP gets a reveal toggle, sign-IN does not. You are
 *    typing a password you have typed before.
 *
 * ⚠️ THE GOOGLE BUTTON IS HERE NOW (2026-09-10, on his word), AND THE PARAGRAPH IT REPLACES IS
 * WORTH KEEPING IN MIND: it refused to ship this button because `signInWithOAuth` redirects
 * through Supabase's callback, which drops any origin not on the project's redirect allow-list —
 * so the button would have been a control on the first screen of the product that silently fails,
 * this repo's own recurring finding that a file which compiles is not a feature that runs.
 *
 * Two things changed. The allow-list entry became answerable at all, because `coachwork.io` went
 * live on 2026-09-09 and before that there was no origin to allow. And the silence is closed:
 * a failed round trip comes back with `?error=...`, which `oauthErrorMessage` turns into the
 * server's own sentence in the SAME alert region a rejected password uses. The allow-list entry
 * itself is still HIS — it is live auth configuration on his project, the security surface.
 *
 * ⚠️ AND IT MATTERS MORE THAN IT LOOKS. Sign-up is iOS-only (his decision, 2026-09-10), and iOS
 * offers Apple and Google AT SIGN-UP — neither of which produces a password. So a coach who signs
 * up by tapping Google has no password to type here, and without this button no way in at all.
 *
 * §1d's verdict was KEEP, Google FIRST and email second, which is the order below. The button is
 * `.ht-button`, not `.ht-button-primary`: §13 allows exactly one accent-tier control per screen
 * and the email submit is it, so a second accent border would flatten the ladder rather than
 * strengthen it.
 */

/* ⚠️ SIGN-UP IS iOS-ONLY, HIS DECISION OF 2026-09-10 — so this screen owes a door rather than a
 * form. Without it a coach with no account meets a login form with no explanation and no way
 * forward, which is the hole he noticed himself: *"why is it bringing us straight to sign in? how
 * one signs up if has no iphone at all?"* The honest answer is that they sign up on the phone, and
 * the screen now says so instead of leaving them to guess.
 *
 * ⚠️ THE URL IS NO LONGER TYPED HERE. It was a file-local constant with the right instinct —
 * "it lives here, once" — and the wrong scope: the invitation a coach actually sends lives in
 * `lib/invite.ts` and carried no link at all, so "once" was true of this file and false of the
 * product. `lib/appLink.ts` is the one place now, shared with the invitation and compared against
 * the phone's copy by a gate (F13 step 1).
 */
const SIGN_UP_URL = TESTFLIGHT_LINK;

type FieldErrors = { email?: string; password?: string; firstName?: string };

/* 🔴 SIGN-UP IS NO LONGER iOS-ONLY (his 2026-09-13 word), and the reason it changed is a real
 * coach: William has a MacBook and no iPhone, so the TestFlight door below was not a door for him
 * at all. It was never only about him — `docs/GO-TO-MARKET.md` puts the critical path to New Year
 * through the coach web app, and "a coach must start on an iPhone" quietly contradicted that for
 * every coach without one.
 *
 * ⚠️ ONE PAGE, TWO MODES, rather than a second route: everything here — the Google button, the
 * alert region, the field errors, the focus rules — is worth exactly as much to somebody creating
 * an account as to somebody returning, and a second page would be a second copy of all of it. */
type Mode = "in" | "up";

export function SignIn() {
  // N4 (2026-10-22): the For-coaches page's "Create your coach account" lands here as `/app?signup` — open on that form.
  const [mode, setMode] = useState<Mode>(() => (opensOnSignUp(window.location.search) ? "up" : "in"));
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  function validate(): FieldErrors {
    const errors: FieldErrors = {};
    // Deliberately forgiving: the only thing worth refusing before a round trip is a field
    // nobody filled in, and an address with no "@" in it. Anything cleverer rejects real
    // addresses, and the server is the actual authority on whether an account exists.
    if (email.trim() === "") {
      errors.email = mode === "up" ? "Enter your email address." : "Enter the email address you signed up with.";
    } else if (!email.includes("@")) errors.email = "That does not look like an email address.";
    if (password === "") errors.password = "Enter your password.";
    // ⚠️ ONLY ON SIGN-UP, and only the FIRST name. A roster row with no name at all reads as a
    // broken account; a missing surname reads as somebody who goes by one name, which is a person.
    else if (mode === "up" && password.length < 6) errors.password = "Use at least 6 characters.";
    if (mode === "up" && firstName.trim() === "") errors.firstName = "Enter your first name.";
    return errors;
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      // Move focus to the first field that is wrong. Announcing an error without moving focus
      // leaves a keyboard user to hunt for it, and a screen-reader user with no idea where it is.
      if (errors.firstName) firstNameRef.current?.focus();
      else if (errors.email) emailRef.current?.focus();
      else passwordRef.current?.focus();
      return;
    }

    setBusy(true);
    if (mode === "up") {
      const outcome = await signUpCoach({ email, password, firstName, lastName });
      setBusy(false);
      if (outcome.kind === "failed") {
        setFormError(outcome.message);
        window.requestAnimationFrame(() => errorRef.current?.focus());
      } else if (outcome.kind === "confirmEmail") {
        // Not an error and not a session: the account exists and is waiting on a link. Said in the
        // NOTICE region rather than the alert one, and the page drops back to Log in so the next
        // thing they do after clicking that link is the thing this screen is now offering.
        setNotice("Account created. Check your email for the confirmation link, then log in.");
        setMode("in");
        setPassword("");
      }
      // On `signedIn` the session lands in storage and `App` re-renders from the auth listener.
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);

    if (error) {
      // The server's own words, never a guess. "Invalid login credentials" deliberately does not
      // say WHICH half was wrong — that is Supabase declining to confirm whether an address has
      // an account, and rewording it here would leak exactly what it withholds.
      setFormError(error.message);
      // `role="alert"` announces it; focus makes it findable.
      window.requestAnimationFrame(() => errorRef.current?.focus());
    }
    // On success the session lands in storage and `App` re-renders from the auth listener.
    // Nothing to do here — a redirect written at this point would be a second router.
  }

  // ⚠️ THIS IS WHAT MAKES THE GOOGLE BUTTON SHIPPABLE, AND WITHOUT IT THE BUTTON IS THE THING
  // THIS FILE'S HEADER REFUSED TO SHIP. An OAuth round trip that fails does not throw: the browser
  // leaves, comes back, and there is simply no session — the coach sees the same login form they
  // started on and has no way to tell a declined consent from a misconfigured project. The failure
  // arrives as `?error=...` in the query, so it is read on arrival and shown in the SAME alert
  // region a rejected password uses.
  //
  // ⚠️ AND THE URL IS CLEANED AFTERWARDS. Left in place, the message would come back on every
  // reload and outlive its own cause — the "an error must clear when its cause does" finding
  // below, one round trip further out. `replaceState` rather than a navigation: this must not add
  // a history entry the back button walks into.
  useEffect(() => {
    const message = oauthErrorMessage(window.location.search);
    if (message === null) return;
    setFormError(message);
    window.history.replaceState(null, "", window.location.pathname + window.location.hash);
    window.requestAnimationFrame(() => errorRef.current?.focus());
  }, []);

  async function onGoogle() {
    setFormError(null);
    setGoogleBusy(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      // One stable URL with no fragment and no query — see `oauthRedirectTarget`. This is the
      // exact string that must sit on the project's redirect allow-list, which is his to add.
      options: { redirectTo: oauthRedirectTarget(window.location) },
    });
    // Reached only when the redirect never happened; on success this page is already gone.
    if (error) {
      setGoogleBusy(false);
      setFormError(error.message);
      window.requestAnimationFrame(() => errorRef.current?.focus());
    }
  }

  // ⚠️ AN ERROR MUST CLEAR WHEN ITS CAUSE DOES, AND THE FIRST CUT OF THIS SCREEN DID NOT.
  // Validation ran only in `onSubmit`, so after an empty submit both messages stayed on screen
  // while the athlete typed the answer into the very field being complained about — and
  // `aria-invalid="true"` stayed with them, so a screen reader went on announcing a field that
  // had been valid for a minute. Found by USING the screen; no check here renders a page, and
  // the state is perfectly consistent, so nothing could have gone red.
  function clearError(field: keyof FieldErrors) {
    setFormError(null);
    setFieldErrors((prev) => (prev[field] === undefined ? prev : { ...prev, [field]: undefined }));
  }

  const emailInvalid = fieldErrors.email !== undefined;
  const firstNameInvalid = fieldErrors.firstName !== undefined;

  /* Switching modes clears what belongs to the other one. Leaving a rejected password in the box
   * while the heading changes to "Create your account" invites somebody to submit it again. */
  function switchTo(next: Mode) {
    setMode(next);
    setFormError(null);
    setNotice(null);
    setFieldErrors({});
    setPassword("");
  }
  const passwordInvalid = fieldErrors.password !== undefined;

  return (
    <main id="main" className="signin-page">
      <div className="signin-frame">
        {/* The brand stands on the GROUND, above the card, so the card holds only the work.
            `BrandLockup` renders the EMITTED mark (`lib/mark.ts`, generated from
            `MarkGeometry`) — the red plate is the one saturated thing on this page besides
            the submit button's outline, which is the accent budget spent exactly once. */}
        <div className="signin-brand">
          <BrandLockup size={34} />
          {/* N4 (2026-10-22): was "Coaching that does the programming for you." — an ENGINE promise (GO-TO-MARKET §0.4)
              on the screen every For-coaches link now lands on. The For-coaches page's own line instead. */}
          <p className="ht-callout signin-tagline">Write the week. See every set.</p>
        </div>

        <div className="signin-card ht-card">
        {/* Google FIRST (§1d), and neutral rather than accent-tier: §13 allows one
            `.ht-button-primary` per screen and the email submit below is it. */}
        <h1 className="ht-headline signin-heading">
          {mode === "in" ? "Log in" : "Create your coach account"}
        </h1>

        {/* ⚠️ ONE ALERT REGION FOR BOTH DOORS, AND IT SITS ABOVE THEM BOTH. It used to live inside
            the form, which was right when the form was the only way in; a Google failure announced
            underneath the Google button and inside the email form would read as a complaint about
            the email form. `role="alert"` announces it, `tabIndex={-1}` makes it findable. */}
        {formError !== null && (
          <div className="signin-form-error" role="alert" ref={errorRef} tabIndex={-1}>
            {formError}
          </div>
        )}

        {/* ⚠️ A SECOND REGION, AND IT IS NOT AN ALERT. "Your account exists, go and confirm it" is
            good news that must not be announced with the same urgency — or the same styling — as a
            refused password. `aria-live="polite"` waits its turn rather than interrupting. */}
        {notice !== null && (
          <p className="signin-notice ht-callout" aria-live="polite">{notice}</p>
        )}

        <button
          className="ht-button signin-google"
          type="button"
          onClick={onGoogle}
          disabled={googleBusy}
        >
          {/* ⚠️ THE FOUR-COLOUR MARK IS REQUIRED, NOT DECORATIVE — Google's Sign-In branding
              guidelines ask for the official logo on this control, so this is the one place in
              the product where a foreign palette is correct and the accent budget does not
              apply. It is `aria-hidden`; the label beside it already says the word. */}
          <GoogleMark />
          <span>{googleBusy ? "Taking you to Google…" : "Continue with Google"}</span>
        </button>
        <span className="ht-visually-hidden" aria-live="polite">
          {googleBusy ? "Taking you to Google" : ""}
        </span>

        {/* A separator that says what it separates. `aria-hidden` because the heading and the
            fields already say it; a screen reader reading the word "or" alone helps nobody. */}
        <p className="signin-or" aria-hidden="true">or</p>

        <form onSubmit={onSubmit} noValidate>
          {/* `noValidate` hands validation to the code above rather than to the browser's
              native bubbles, which cannot be styled, vanish on blur, and read inconsistently
              to screen readers. Having taken that over, the code owes a real message per
              field — which is what `aria-describedby` below points at. */}

          {mode === "up" && (
            <>
              <div className="ht-field signin-field">
                <label className="ht-field-label" htmlFor="firstName">First name</label>
                <input
                  id="firstName"
                  ref={firstNameRef}
                  className="ht-input"
                  type="text"
                  value={firstName}
                  onChange={(e) => { setFirstName(e.target.value); clearError("firstName"); }}
                  autoComplete="given-name"
                  aria-invalid={firstNameInvalid}
                  aria-describedby={firstNameInvalid ? "firstName-error" : undefined}
                />
                {firstNameInvalid && (
                  <p className="ht-field-error" id="firstName-error">
                    <span aria-hidden="true">✕</span>
                    {fieldErrors.firstName}
                  </p>
                )}
              </div>
              <div className="ht-field signin-field">
                <label className="ht-field-label" htmlFor="lastName">Last name</label>
                <input
                  id="lastName"
                  className="ht-input"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                />
              </div>
            </>
          )}

          <div className="ht-field signin-field">
            <label className="ht-field-label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              ref={emailRef}
              className="ht-input"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearError("email");
              }}
              autoComplete="email"
              // `aria-invalid` is set from OUR state, never from `:invalid`, so nothing turns
              // red while a half-typed address is still being typed.
              aria-invalid={emailInvalid}
              aria-describedby={emailInvalid ? "email-error" : undefined}
            />
            {emailInvalid && (
              <p className="ht-field-error" id="email-error">
                {/* A mark as well as a colour. The design system's rule — "convey information
                    with more than colour alone" — is also a WCAG requirement. */}
                <span aria-hidden="true">✕</span>
                {fieldErrors.email}
              </p>
            )}
          </div>

          <div className="ht-field signin-field">
            <label className="ht-field-label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              ref={passwordRef}
              className="ht-input"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                clearError("password");
              }}
              autoComplete={mode === "in" ? "current-password" : "new-password"}
              aria-invalid={passwordInvalid}
              aria-describedby={passwordInvalid ? "password-error" : undefined}
            />
            {passwordInvalid && (
              <p className="ht-field-error" id="password-error">
                <span aria-hidden="true">✕</span>
                {fieldErrors.password}
              </p>
            )}
          </div>

          <button className="ht-button ht-button-primary signin-submit" type="submit" disabled={busy}>
            {busy
              ? (mode === "in" ? "Logging in…" : "Creating your account…")
              : (mode === "in" ? "Log in" : "Create account")}
          </button>
          {/* Disabled ONLY while a request is in flight — which is a fact about the request,
              not a judgement about the form, and is the one case where disabling says something
              true. `aria-live` announces the change of state to a screen reader. */}
          <span className="ht-visually-hidden" aria-live="polite">
            {busy ? (mode === "in" ? "Logging in" : "Creating your account") : ""}
          </span>
        </form>

        {/* ⚠️ THE TESTFLIGHT DOOR IS STILL HERE, AND ONLY IN THE SIGN-UP MODE. This page now makes
            COACH accounts; an athlete who lands here still needs the phone, and saying so is the
            difference between a door and a dead end. */}
        <p className="signin-signup">
          {mode === "in" ? (
            <>
              New here?{" "}
              <button className="ht-link" type="button" onClick={() => switchTo("up")}>
                Create a coach account
              </button>
            </>
          ) : (
            <>
              Already have one?{" "}
              <button className="ht-link" type="button" onClick={() => switchTo("in")}>
                Log in
              </button>
              <br />
              Training with a coach?{" "}
              <a className="ht-link" href={SIGN_UP_URL}>
                Get the iPhone app
              </a>
            </>
          )}
        </p>
        </div>
      </div>
    </main>
  );
}
